import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { GenericService } from '../../../services/generic.service';

interface WordPressPost {
  id: number;
  date: string;
  date_gmt: string;
  link: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  categories: number[];
  jetpack_featured_media_url?: string;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
  };
}

interface WordPressCategory {
  id: number;
  count: number;
  name: string;
  slug: string;
  parent: number;
}

interface CategoryWithChildren extends WordPressCategory {
  children?: CategoryWithChildren[];
  isExpanded?: boolean;
}

@Component({
  selector: 'app-blogs',
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  templateUrl: './blogs.component.html',
  styleUrl: './blogs.component.scss'
})
export class BlogsComponent implements OnInit, OnDestroy {
  blogs: WordPressPost[] = [];
  allBlogs: WordPressPost[] = [];
  categories: CategoryWithChildren[] = [];
  allCategories: WordPressCategory[] = [];
  selectedCategoryId: number | null = null;
  isLoading = true;
  isLoadingMore = false;
  skeletonItems = Array(6).fill(0);
  currentPage = 1;
  perPage = 100;
  hasMore = true;
  private scrollThrottleTimer: any;
  private hoverTimer: any;

  constructor(private genericService: GenericService) { }

  ngOnInit(): void {
    this.loadCategories();
    this.loadBlogs();
  }

  ngOnDestroy(): void {
    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
    }
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
    }

    this.scrollThrottleTimer = setTimeout(() => {
      if (this.isLoadingMore || !this.hasMore) {
        return;
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMoreBlogs();
      }
    }, 100);
  }

  loadCategories(): void {
    this.loadAllCategories();
  }

  private loadAllCategories(page: number = 1, accumulatedCategories: WordPressCategory[] = []): void {
    const apiUrl = `https://blog.gradpilots.com/wp-json/wp/v2/categories?per_page=100&page=${page}`;

    this.genericService.getThirdPartyResponse<WordPressCategory[]>(apiUrl).subscribe({
      next: (categories) => {
        const allFetched = [...accumulatedCategories, ...categories];

        if (categories.length === 100) {
          this.loadAllCategories(page + 1, allFetched);
        } else {
          const filteredCategories = allFetched.filter(cat =>
            cat.slug.toLowerCase() !== 'uncategorized' &&
            cat.slug.toLowerCase() !== 'people'
          );

          this.allCategories = filteredCategories;
          console.log('Total categories loaded:', this.allCategories.length);
        }
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        if (accumulatedCategories.length > 0) {
          const filteredCategories = accumulatedCategories.filter(cat =>
            cat.slug.toLowerCase() !== 'uncategorized' &&
            cat.slug.toLowerCase() !== 'people'
          );

          this.allCategories = filteredCategories;
        }
      }
    });
  }

  buildCategoryTree(categories: WordPressCategory[]): CategoryWithChildren[] {
    const categoryMap = new Map<number, CategoryWithChildren>();
    const rootCategories: CategoryWithChildren[] = [];

    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [], isExpanded: false });
    });

    categories.forEach(cat => {
      const category = categoryMap.get(cat.id)!;

      if (cat.parent === 0) {
        rootCategories.push(category);
      } else {
        const parent = categoryMap.get(cat.parent);
        if (parent) {
          parent.children!.push(category);
        }
      }
    });

    rootCategories.forEach(parent => {
      if (parent.children && parent.children?.length > 0) {
        parent.children = parent.children.filter(child => this.getCategoryCount(child.id) > 0);
        parent.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    const finalParents = rootCategories.filter(parent => {
      const hasOwnPosts = this.getCategoryCount(parent.id) > 0;
      const hasChildrenWithPosts = parent.children && parent.children?.length > 0;
      return hasOwnPosts || hasChildrenWithPosts;
    });

    finalParents.sort((a, b) => a.name.localeCompare(b.name));

    return finalParents;
  }

  toggleCategory(category: CategoryWithChildren): void {
    if (category.children && category.children.length > 0) {
      category.isExpanded = !category.isExpanded;
    }
  }

  onCategoryHover(category: CategoryWithChildren): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }

    if (category.children && category.children.length > 0) {
      category.isExpanded = true;
    }

  }

  onCategoryLeave(category: CategoryWithChildren): void {
    if (category.children && category.children.length > 0) {
        category.isExpanded = false;
        if (this.hoverTimer) {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = null;
        }
    }
  }

  onImageError(event: any) {
    event.target.src = '../../../../assets/images/slider-image.jpg';
  }

  loadBlogs(): void {
    this.isLoading = true;
    this.currentPage = 1;
    this.allBlogs = [];
    this.hasMore = true;

    const apiUrl = `https://blog.gradpilots.com/wp-json/wp/v2/posts?_embed&per_page=${this.perPage}&page=${this.currentPage}`;

    this.genericService.getThirdPartyResponse<WordPressPost[]>(apiUrl).subscribe({
      next: (posts) => {
        this.allBlogs = posts;
        this.filterBlogs();
        this.isLoading = false;
        this.hasMore = posts.length === this.perPage;
        if (this.allCategories.length > 0) {
          this.categories = this.buildCategoryTree(this.allCategories);
        }
      },
      error: (error) => {
        console.error('Error loading blogs:', error);
        this.isLoading = false;
        this.hasMore = false;
      }
    });
  }

  loadMoreBlogs(): void {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;

    const apiUrl = `https://blog.gradpilots.com/wp-json/wp/v2/posts?_embed&per_page=${this.perPage}&page=${this.currentPage}`;

    this.genericService.getThirdPartyResponse<WordPressPost[]>(apiUrl).subscribe({
      next: (posts) => {
        if (posts.length > 0) {
          this.allBlogs = [...this.allBlogs, ...posts];
          this.filterBlogs();
          this.hasMore = posts.length === this.perPage;
          if (this.allCategories.length > 0) {
            this.categories = this.buildCategoryTree(this.allCategories);
          }
        } else {
          this.hasMore = false;
        }
        this.isLoadingMore = false;
      },
      error: (error) => {
        console.error('Error loading more blogs:', error);
        this.isLoadingMore = false;
        this.hasMore = false;
        this.currentPage--;
      }
    });
  }

  filterBlogs(): void {
    if (this.selectedCategoryId === null) {
      this.blogs = this.allBlogs;
    } else {
      this.blogs = this.allBlogs.filter(blog =>
        blog.categories && blog.categories.includes(this.selectedCategoryId!)
      );
    }
  }

  selectCategory(categoryId: number | null): void {
    this.selectedCategoryId = categoryId;

    this.filterBlogs();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getCategoryCount(categoryId: number): number {
    return this.allBlogs.filter(blog =>
      blog.categories && blog.categories.includes(categoryId)
    ).length;
  }

  getFeaturedImage(post: WordPressPost): string {
    if (post.jetpack_featured_media_url) {
      return post.jetpack_featured_media_url;
    }
    if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    return '../../../../assets/images/slider-image.jpg';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  stripHtml(html: string): string {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  calculateReadingTime(post: WordPressPost): number {
    const content = this.stripHtml(post.content.rendered);
    const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    const readingTime = Math.ceil(wordCount / 200);
    return readingTime > 0 ? readingTime : 1;
  }
}
